import React, { useCallback, useEffect } from "react"
import { ISharedContext, ViewModes, Dispatch } from "./registration_form_component"
import { FormHelpers } from "./form_helpers"
import { formatCoveredLives, serializeRegExp } from "./helpers"
import * as data from "../data"
import { FormStatus } from "../wizard"
import { IClientConfig } from "../clients/config"

interface IClaimValueFormProps extends ISharedContext {
  coverageType: {
    field: keyof data.FormCoverageEstimates
    title: string
  }
}

export function ClaimValueForm(props: IClaimValueFormProps): React.ReactNode {
  const coverageType = props.coverageType
  const table = props.form?.[coverageType.field]

  if (props.form?.insurer?.type && !data.HasCoverageEstimatesSections.has(props.form.insurer.type)) {
    return null
  }

  return (
    <div className="panel panel-default">
      <div className="panel-heading">
        <h1 className="panel-title">
          <span className="required">{coverageType.title} Coverage Estimates</span>
        </h1>
        <AnyCoveredLives
          field={coverageType.field}
          table={table}
          dispatch={props.dispatch}
        />
      </div>

      <div className="panel-body">
        <p style={{ marginBottom: ".5em" }}>
          For reporting relevant to this data collection initiative, please provide estimates for your organization&apos;s
          overall book of business and individual lines of business based on the average for a single month. Provide
          these counts even if your organization currently is exempted from data submission based on state requirements.
          If your organization has lines of business that do not align with the displayed categories, please report them
          under &lsquo;Other&rsquo;. When reporting, <strong>round to the next highest whole number and do not include
          punctuation.</strong>
        </p>
        <p style={{ marginBottom: 0 }}>
          <strong>Note:</strong>{" "}
          <em>
            Grand total values may not be the sum of the detailed categories since the provided list of individual lines
            of business may include overlaps. If counts are either unavailable or not applicable, please report a value
            of &quot;0&quot; (zero). Onpoint&apos;s Data Operations team will follow up to confirm any unexpected responses.
          </em>
        </p>
      </div>

      <div className="form-horizontal">
        <ClaimValueTable
          key={coverageType.field}
          title={coverageType.title}
          field={coverageType.field}
          {...props}
        />
      </div>
    </div>
  )
}

function AnyCoveredLives({
  field,
  table,
  dispatch,
}: {
  field: keyof data.FormCoverageEstimates
  table?: data.ICoverageEstimatesTable
  dispatch: Dispatch
}) {
  // useCallback may not be needed here since it is not being passed to a child?
  const onChange = useCallback((e: { currentTarget: { value: string } }) => {
    const value = e.currentTarget.value
    if (!(value === data.YES || value === data.NO)) {
      console.error("Ignoring unexpected value; expected 'yes' or 'no', got:", value)
      return
    }

    // If changed to "no", zero all fields (and copy). If changed to "yes", then the fields should already be zeroed
    // unless they're missing from the saved data (which could happen if a new plan row was added since the response
    // was saved). In that scenario, we want the new rows zeroed out too.
    const coverageEstimatesTable = FormHelpers.zeroClaimValues(table)

    // switch the radio button value on the copy of the table
    coverageEstimatesTable.any_covered_lives = value

    const delta: data.IRegistrationForm = {}
    delta[field] = coverageEstimatesTable

    dispatch({
      action: "UPDATE_FORM",
      form: delta,
    })
  }, [field, table, dispatch])

  useEffect(() => {
    if (typeof table?.any_covered_lives === "undefined") {
      onChange({
        currentTarget: {
          value: data.YES,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const legendId = `${field}_any_covered_lives_legend`

  return (
    <div role="radiogroup" className="any-covered-lives" aria-labelledby={legendId}>
      <p className="required" id={legendId} style={{display: "inline-block", paddingRight: "1.0em", marginBottom: "0"}}>
        Does your organization have any {field} covered lives to report?
      </p>
      <label className="radio-inline" style={{paddingRight: "1.0em"}}>
        <input
          type="radio"
          value={data.YES}
          name={`${field}[any_covered_lives]`}
          onChange={onChange}
          checked={table?.any_covered_lives === data.YES}
          required={true}
          data-fv-notempty-message="Please select &quot;Yes&quot; or &quot;No&quot;"
        />
        Yes
      </label>
      <label className="radio-inline">
        <input
          type="radio"
          value={data.NO}
          name={`${field}[any_covered_lives]`}
          onChange={onChange}
          checked={table?.any_covered_lives === data.NO}
          required={true}
          data-fv-notempty-message="Please select &quot;Yes&quot; or &quot;No&quot;"
        />
        No
      </label>
    </div>
  )
}

interface IClaimValueTableProps extends ISharedContext {
  readOnly?: boolean
  field: keyof data.FormCoverageEstimates
  title: string
}

const SERIALIZED_VALUE_MATCH_REGEX = serializeRegExp(/^[0-9.$,]+$/)

const areCellsDisabled = (table?: data.ICoverageEstimatesTable): boolean => {
  return !table?.any_covered_lives || table?.any_covered_lives === data.NO
}

export const ClaimValueTable = (props: IClaimValueTableProps) => {
  const {
    form,
    field,
    title,
    onInputChange,
    setFormValue,
  } = props

  /*
   * Normalize and format numeric fields after user finishes typing.
   *
   * 1. Strip non-numeric characters.
   * 2. Parse number, rounding decimals to next highest integer.
   * 3. Format with commas.
   *
   * If parsing fails, value will be blanked.
   */
  const onBlur = useCallback((event: React.FormEvent<HTMLInputElement>): void => {
    const { value, path } = FormHelpers.getFormChangeArgsFromEvent(event)

    if (typeof value !== "string") {
      // Value will always be a string, unless there is a checkbox or boolean
      // or something. (Still not sure how this can happen.)
      // If it isn't a string, we will just ignore it and not perform any normalization.
      throw new Error(`Got unexpected non-string value from form field: [${path.join(",")}]`)
    }

    const formatted = formatCoveredLives(value)

    setFormValue(path, formatted)
  }, [setFormValue])

  let readOnly = false
  if (typeof props.readOnly !== "undefined") {
    readOnly = props.readOnly
  }

  return (
    <table className="table" style={{ marginBottom: 0 }}>
      <thead>
        <tr>
          <th>{title}</th>
          <th className="text-right">Covered Lives</th>
          <th className="text-right">Claim Volume</th>
          <th className="text-right">Claim Value (USD)</th>
        </tr>
      </thead>

      <tbody>
        <tr className="highlighted-total">
          <td>
            <strong>Grand Total</strong>
          </td>
          {FormHelpers.claimValueGroups().map((claimValue) => {
            const path = [field, "claim_value", claimValue, "totals"]

            const table = form?.[field]
            const value = table?.claim_value?.[claimValue]?.totals ?? ""
            const disabled = areCellsDisabled(table)

            let inputClasses = "compact text-right"
            if (disabled) {
              inputClasses += " disabled"
            }

            return (
              <td className="td--no-padding" key={claimValue}>
                <input
                  type="text"
                  required={true}
                  maxLength={20}
                  data-fv-regex-match={SERIALIZED_VALUE_MATCH_REGEX}
                  placeholder={readOnly ? "-" : "Value"}
                  className={inputClasses}
                  readOnly={readOnly}
                  disabled={disabled}
                  name={FormHelpers.inputName(path)}
                  onChange={onInputChange}
                  value={value}
                  onBlur={onBlur}
                />
              </td>
            )
          })}
        </tr>
      </tbody>

      {FormHelpers.claimValues().map((row) => {
        return (
          <tbody key={row.heading}>
            {row.subheadings.map((subheading) => {
              const subheadingsIndex = row.subheadings.indexOf(subheading)
              const subfield = row.subfields[subheadingsIndex]
              if (subfield == undefined) {
                throw new Error(`subfield is undefined for row.subfields[${subheadingsIndex}]`);
              }

              // Medicare Part D is only on pharmacy grid
              if (subfield === "part_d" && field !== "pharmacy") {
                return null
              }

              // Don't show D-SNP for dental grid (DO-562)
              if (subfield === "d_snp" && field === "dental") {
                return null
              }

              const key = `${field}_${row.field as string}_${subfield}`
              const table = form?.[field]
              const disabled = areCellsDisabled(table)

              let inputClasses = "compact text-right"
              if (disabled) {
                inputClasses += " disabled"
              }

              return (
                <tr key={key}>
                  <td>
                    <div className="padding--left-sm">{subheading}</div>
                  </td>

                  {FormHelpers.claimValueGroups().map((claimValue) => {
                    const path = [field, "claim_value", claimValue, row.field as string, subfield]

                    const values = table?.claim_value?.[claimValue]?.[row.field] as Record<string, string> | undefined
                    const value = values?.[subfield] ?? (table?.any_covered_lives === "no" ? "0" : "")

                    return (
                      <td className="td--no-padding" key={`${key}_${claimValue}`}>
                        <input
                          type="text"
                          required={true}
                          maxLength={20}
                          data-fv-regex-match={SERIALIZED_VALUE_MATCH_REGEX}
                          placeholder={readOnly ? "-" : "Value"}
                          className={inputClasses}
                          readOnly={readOnly}
                          disabled={disabled}
                          name={FormHelpers.inputName(path)}
                          value={value}
                          onChange={onInputChange}
                          onBlur={onBlur}
                        />
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        )
      })}
    </table>
  )
}

/**
 * You need to add your own:
 * - key
 * - title
 * - field
 * - form
 *
 * @returns {object} Partially-filled props for use with read-only version of this component
 */
ClaimValueTable.readOnlyProps = function() {
  return {
    readOnly: true,
    config: {} as IClientConfig,
    client: {} as data.IClient,
    loaded: true,
    attachment: null,
    status: FormStatus.Complete,
    editable: false,
    csrf_expired: false,
    continue_token_needed: false,
    current_step: -1,
    max_step: -1,
    canEditText: false,
    canEditSubmitterCode: false,
    active_submitters: [],
    urls: {
      root: "",
      get_form: "",
      attachment: "",
      continue: "",
      save: "",
      restart: "",
      complete: "",
      print: "",
      zip_lookup: "",
    },
    short_id: null,
    view_mode: ViewModes.NORMAL, // ignored for review/submit (printable by default)
    dispatch: () => {
      /* noop */
    },
    onInputChange: () => {
      /* noop */
    },
    setFormValue: () => {
      /* noop */
    },
  }
}
